import { type ReactNode, useState } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import AdminLayout from '@/components/AdminLayout'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { MoreHorizontal, Plus, Star } from 'lucide-react'

type AdminEvent = {
  id: string
  title: string
  date: string | null
  status: string
  page_kind: 'event' | 'fundraiser'
  featured: boolean
  organization_name: string
  claimed: boolean
  city: string | null
  state: string | null
  page_views: number
}

type Props = {
  events: AdminEvent[]
  filters: { search: string; status: string; claimed: string }
}

function formatDate(dateStr: string | null) {
  if (!dateStr) return 'Fundraiser'
  const d = dateStr.includes('T') ? new Date(dateStr) : new Date(dateStr + 'T00:00:00')
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

export default function AdminEventsIndex({ events, filters }: Props) {
  const [search, setSearch] = useState(filters.search)
  const [deleteTarget, setDeleteTarget] = useState<AdminEvent | null>(null)

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    router.get('/admin/events', { search, status: filters.status, claimed: filters.claimed }, { preserveState: true })
  }

  function filterByStatus(status: string) {
    router.get('/admin/events', { search: filters.search, status, claimed: filters.claimed }, { preserveState: true })
  }

  function filterByClaimed(claimed: string) {
    router.get('/admin/events', { search: filters.search, status: filters.status, claimed }, { preserveState: true })
  }

  function toggleFeatured(eventId: string, currentValue: boolean) {
    router.patch(`/admin/events/${eventId}`, { featured: currentValue ? 'false' : 'true' }, { preserveState: true })
  }

  function archiveEvent(eventId: string) {
    router.patch(`/admin/events/${eventId}/archive`, {}, { preserveState: true })
  }

  function confirmDelete() {
    if (!deleteTarget) return
    router.delete(`/admin/events/${deleteTarget.id}`)
    setDeleteTarget(null)
  }

  return (
    <>
      <Head title="Admin - Events" />
      <div>
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-semibold tracking-tight font-serif">Events</h1>
          <Button asChild>
            <Link href="/admin/events/new">
              <Plus className="mr-2 size-4" />
              New Seeded Event
            </Link>
          </Button>
        </div>

        <div className="mt-6 flex items-center gap-3">
          <form onSubmit={handleSearch} className="max-w-sm flex-1">
            <Input
              placeholder="Search by title or organization..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </form>
          <div className="flex gap-1">
            <Button variant={!filters.status ? 'secondary' : 'outline'} size="sm" onClick={() => filterByStatus('')}>All</Button>
            <Button variant={filters.status === 'published' ? 'secondary' : 'outline'} size="sm" onClick={() => filterByStatus('published')}>Published</Button>
            <Button variant={filters.status === 'draft' ? 'secondary' : 'outline'} size="sm" onClick={() => filterByStatus('draft')}>Draft</Button>
            <Button variant={filters.status === 'archived' ? 'secondary' : 'outline'} size="sm" onClick={() => filterByStatus('archived')}>Archived</Button>
          </div>
          <div className="flex gap-1 border-l pl-3">
            <Button variant={!filters.claimed ? 'secondary' : 'outline'} size="sm" onClick={() => filterByClaimed('')}>All</Button>
            <Button variant={filters.claimed === 'unclaimed' ? 'secondary' : 'outline'} size="sm" onClick={() => filterByClaimed('unclaimed')}>Unclaimed</Button>
            <Button variant={filters.claimed === 'claimed' ? 'secondary' : 'outline'} size="sm" onClick={() => filterByClaimed('claimed')}>Claimed</Button>
          </div>
        </div>

        <Table className="mt-6">
          <TableHeader>
            <TableRow>
              <TableHead>Title</TableHead>
              <TableHead>Organization</TableHead>
              <TableHead>Date</TableHead>
              <TableHead>Location</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-center">Featured</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {events.map(event => (
              <TableRow key={event.id}>
                <TableCell className="font-medium">{event.title}</TableCell>
                <TableCell className="text-muted-foreground">
                  {event.claimed ? (
                    event.organization_name
                  ) : (
                    <Badge variant="outline" className="text-muted-foreground">Unclaimed</Badge>
                  )}
                </TableCell>
                <TableCell className="text-muted-foreground">{formatDate(event.date)}</TableCell>
                <TableCell className="text-muted-foreground">
                  {[event.city, event.state].filter(Boolean).join(', ') || '—'}
                </TableCell>
                <TableCell>
                  <div className="flex items-center gap-1.5">
                    <Badge variant={event.status === 'published' ? 'default' : 'secondary'} className="capitalize">
                      {event.status}
                    </Badge>
                    {event.page_kind === 'fundraiser' && <Badge variant="outline">Fundraiser</Badge>}
                  </div>
                </TableCell>
                <TableCell className="text-center">
                  <button
                    onClick={() => toggleFeatured(event.id, event.featured)}
                    className="inline-flex items-center justify-center rounded-md p-1.5 transition-colors hover:bg-muted"
                    title={event.featured ? 'Remove from featured' : 'Add to featured'}
                  >
                    <Star className={`size-4 ${event.featured ? 'fill-primary text-primary' : 'text-muted-foreground/40'}`} />
                  </button>
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button asChild variant="ghost" size="sm">
                      <Link href={`/admin/events/${event.id}/edit`}>Edit</Link>
                    </Button>
                    {!event.claimed && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="size-8" title="More actions">
                            <MoreHorizontal className="size-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {event.status !== 'archived' && (
                            <DropdownMenuItem onClick={() => archiveEvent(event.id)}>
                              Archive
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => setDeleteTarget(event)}
                          >
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {events.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                  No events found.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete event</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete &ldquo;{deleteTarget?.title}&rdquo;? This permanently removes the event and cannot be undone. Events with ticket sales or donations can&rsquo;t be deleted — archive them instead.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmDelete}>Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

AdminEventsIndex.layout = (page: ReactNode) => <AdminLayout>{page}</AdminLayout>
